// v-tilt — pointer-driven 3D tilt plus cursor spotlight for tiles.
// Writes --mx/--my (used by the `spotlight` utility) and a small rotateX/Y
// on the element; resets on leave. Skips touch and reduced-motion users,
// where the effect would either fight scrolling or be unwanted.
import type { Directive } from 'vue';

const MAX_DEG = 6;

const enabled = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

type TiltEl = HTMLElement & { __tilt?: { move: (e: PointerEvent) => void; leave: () => void } };

export const tilt: Directive<TiltEl> = {
  mounted(el) {
    if (!enabled()) return;
    el.style.transformStyle = 'preserve-3d';
    el.style.willChange = 'transform';

    const move = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width;
      const py = (e.clientY - r.top) / r.height;
      el.style.setProperty('--mx', `${(px * 100).toFixed(1)}%`);
      el.style.setProperty('--my', `${(py * 100).toFixed(1)}%`);
      const rx = ((0.5 - py) * MAX_DEG * 2).toFixed(2);
      const ry = ((px - 0.5) * MAX_DEG * 2).toFixed(2);
      el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-4px)`;
    };
    const leave = () => {
      el.style.transform = '';
    };

    el.addEventListener('pointermove', move);
    el.addEventListener('pointerleave', leave);
    el.__tilt = { move, leave };
  },
  unmounted(el) {
    if (!el.__tilt) return;
    el.removeEventListener('pointermove', el.__tilt.move);
    el.removeEventListener('pointerleave', el.__tilt.leave);
    delete el.__tilt;
  },
};
